import {
  Card,
  CardBody,
  HStack,
  Text,
  VStack,
  Tag,
  TagLeftIcon,
  LinkBox,
  LinkOverlay,
} from '@chakra-ui/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { getUserFullName } from '@duke-hq/lib';
import { UserIcon } from 'lucide-react';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import { EnrolmentHealthBadge } from '@duke-hq/ui/components/EnrolmentHealthBadge/EnrolmentHealthBadge';
import type { StudentEnrolmentHealth } from '@duke-hq/shared-constants/studentEnrolment';
import { CourseStatusSchema, type CourseStatus } from '@duke-hq/shared-constants/courseStatus';

export type StudentEnrolmentCardProps = {
  enrolmentId: number;
  health: StudentEnrolmentHealth;
  course: {
    title: string;
    identifier: string | null;
    status: CourseStatus;
  };
  consultant: {
    id: string;
    first_name: string;
    last_name: string | null;
  } | null;
  consultantSuffix?: string;
  extraTags?: React.ReactNode;
};

export function StudentEnrolmentCard({
  enrolmentId,
  health,
  course,
  consultant,
  consultantSuffix,
  extraTags,
}: StudentEnrolmentCardProps) {
  const consultantFullName = consultant ? getUserFullName(consultant) : undefined;

  return (
    <Card
      as={LinkBox}
      variant="outline"
      width="full"
      role="group"
      transition="all 0.2s ease-in-out"
      _hover={{ boxShadow: 'sm', borderColor: 'brand.500' }}
    >
      <CardBody>
        <HStack spacing={4} align="start">
          <LinkOverlay as={ReactRouterLink} to={`/enrolment/${enrolmentId}`}>
            <Text
              color="brand.500"
              fontWeight="semibold"
              position="relative"
              display="inline-block"
              _after={{
                content: '""',
                position: 'absolute',
                width: 'full',
                height: 'px',
                bottom: 'px',
                left: 0,
                bg: 'currentColor',
                transform: 'scaleX(0)',
                transformOrigin: 'bottom left',
                transition: 'transform 0.2s ease-out',
              }}
              _groupHover={{
                _after: {
                  transform: 'scaleX(1)',
                },
              }}
            >
              {enrolmentId}
            </Text>
          </LinkOverlay>

          <VStack align="start">
            <EnrolmentHealthBadge health={health} />

            <VStack align="start" spacing={0}>
              <Text fontWeight="extrabold">{course.title}</Text>
              <HStack spacing={2}>
                {course.status === CourseStatusSchema.enum.SUPERSEDED ? (
                  <ResourceStatusBadge status={course.status} />
                ) : null}
                {course.identifier != null ? (
                  <Text color="fg.muted" fontSize="sm">
                    {course.identifier}
                  </Text>
                ) : null}
              </HStack>
            </VStack>

            <HStack spacing={4}>
              {consultant != null ? (
                <Tag size="sm" colorScheme="green" variant="outline">
                  <TagLeftIcon as={UserIcon} />
                  {consultantFullName}
                  {consultantSuffix}
                </Tag>
              ) : null}
              {extraTags}
            </HStack>
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
}
